import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {DateAdapter} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {
    applyTime,
    calendarDaysBetween,
    endOfDay,
    formatDateTimeFromDateToUi,
    LocalizationService,
    startOfDay,
} from '@npck/libs/commons';
import dayjs from 'dayjs';
import {debounceTime, distinctUntilChanged, filter} from 'rxjs';
import {CalendarComponent} from '../calendar/calendar.component';

@Component({
    selector: 'ui-kit-date-range-picker',
    templateUrl: 'date-range-picker.component.html',
    styleUrl: 'date-range-picker.component.scss',
    standalone: false
})
export class DateRangePickerComponent implements OnInit, OnChanges, AfterViewInit {
    @ViewChild('picker') picker: CalendarComponent;
    @ViewChild('matSelect') matSelect: MatSelect;
    @Input() form: FormGroup<DateRangeForm>;
    @Input() todaySelection: boolean;
    @Input() singleMonth = false;
    @Input() withTime = false;
    @Input() startTimeControl: FormControl<string>;
    @Input() endTimeControl: FormControl<string>;
    @Input() placeholder = 'i18n_lib_commonDateRangePlaceholder';
    @Output() dateChange = new EventEmitter<void>();
    disabled: boolean;
    PickerRange = PickerRange;
    dateRange: PickerRange;
    displayValue: string;
    sortedPickerRange = [
        PickerRange.TODAY,
        PickerRange.CURRENT_WEEK,
        PickerRange.CURRENT_MONTH,
        PickerRange.PREVIOUS_MONTH,
        PickerRange.SELECT_RANGE,
    ];

    constructor(
        private dateAdapter: DateAdapter<any>,
        public localizationService: LocalizationService,
    ) {
    }

    ngOnInit(): void {
        const initialRange = this.form.value;
        if (this.form.valid && initialRange.dateFrom && initialRange.dateTo) {
            this.dateRange = PickerRange.SELECT_RANGE;
            this.modifyDisplayValue();
        }
        this.localizationService.language$.subscribe(language => {
            this.dateAdapter.setLocale(language.locale);
            this.form.valid && this.modifyDisplayValue();
        });
        this.form.valueChanges
            .pipe(
                filter(() => this.form.valid),
                debounceTime(10),
                distinctUntilChanged(),
            )
            .subscribe(() => this.modifyDisplayValue());
        this.form.valueChanges
            .pipe(filter(({dateFrom, dateTo}) => !dateFrom && !dateTo))
            .subscribe(() => {
                this.dateRange = null;
                this.displayValue = null;
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.withTime && !changes.withTime.firstChange && this.form.valid) {
            const {dateFrom, dateTo} = this.form.value;
            this.setRange(dateFrom, dateTo);
            this.modifyDisplayValue();
        }
    }

    ngAfterViewInit(): void {
        if (this.todaySelection) {
            setTimeout(() => {
                this.dateRange = PickerRange.TODAY;
                this.handleSelection(PickerRange.TODAY);
                this.displayValue = this.formatDate();
            }, 0);
        }
    }

    handleSelection(event: PickerRange): void {
        switch (event) {
            case PickerRange.TODAY:
                const today = this.getTodayRange();
                this.setRange(today[0], today[1]);
                this.dateChange.emit();
                break;
            case PickerRange.CURRENT_WEEK:
                const currentWeek = this.getWeekRange();
                this.setRange(currentWeek[0], currentWeek[1], new Date());
                this.dateChange.emit();
                break;
            case PickerRange.CURRENT_MONTH:
                const currentMonth = this.getMonthRange();
                this.setRange(currentMonth[0], currentMonth[1]);
                this.dateChange.emit();
                break;
            case PickerRange.PREVIOUS_MONTH:
                const previousMonth = this.getMonthRange(-1);
                this.setRange(previousMonth[0], previousMonth[1]);
                this.dateChange.emit();
                break;
            case PickerRange.SELECT_RANGE:
                this.picker.open();
                break;
        }
    }

    selectDate(): void {
        this.picker._applyPendingSelection();
        const {dateFrom, dateTo} = this.form.value;
        if (dateFrom && dateTo) {
            this.setRange(dateFrom, dateTo, dateFrom);
        }
        this.picker.close();
        this.modifyDisplayValue();
        this.dateChange.emit();
    }

    shiftRange(multiplier: number): void {
        const startDate = this.form.value.dateFrom;
        const endDate = this.form.value.dateTo;
        switch (this.dateRange) {
            case PickerRange.TODAY:
                startDate.setDate(startDate.getDate() + multiplier);
                endDate.setDate(endDate.getDate() + multiplier);
                this.setRange(startDate, endDate);
                this.dateChange.emit();
                break;
            case PickerRange.CURRENT_WEEK:
                const currentWeek = this.getWeekRange(multiplier * 7, startDate);
                this.setRange(currentWeek[0], currentWeek[1]);
                this.dateChange.emit();
                break;
            case PickerRange.CURRENT_MONTH:
            case PickerRange.PREVIOUS_MONTH:
                this.shiftMonth(multiplier);
                this.dateChange.emit();
                break;
            case PickerRange.SELECT_RANGE:
                if (this.isFullMonthSelected(startDate, endDate)) {
                    this.shiftMonth(multiplier);
                } else {
                    const countDays = (calendarDaysBetween(startDate, endDate) + 1) * multiplier;
                    startDate.setDate(startDate.getDate() + countDays);
                    endDate.setDate(endDate.getDate() + countDays);
                    this.setRange(startDate, endDate);
                }
                this.dateChange.emit();
                break;
        }
    }

    modifyDisplayValue(): void {
        this.displayValue = null;
        this.displayValue = this.formatDate();
    }

    formatDate(): string {
        const startDate = this.form.value.dateFrom;
        const endDate = this.form.value.dateTo;
        if (!startDate || !endDate) {
            return null;
        }
        if (this.withTime) {
            return `${formatDateTimeFromDateToUi(startDate)} - ${formatDateTimeFromDateToUi(endDate)}`;
        }
        const startMonth = startDate.getMonth();
        const endMonth = endDate.getMonth();
        const startYear = startDate.getFullYear();
        const endYear = endDate.getFullYear();
        const currentYear = new Date().getFullYear();
        let startFormattedDate: string;
        let endFormattedDate: string;

        if (startYear === endYear && ![startYear, endYear].some(year => year !== currentYear)) {
            startFormattedDate = dayjs(startDate).format('DD MMMM');
            endFormattedDate = dayjs(endDate).format('DD MMMM YYYY');
            if (startMonth === endMonth) {
                if (this.isFullMonthSelected(startDate, endDate)) {
                    const month = dayjs(endDate).format('MMMM');
                    return month.charAt(0).toUpperCase() + month.slice(1);
                }
                startFormattedDate = dayjs(startDate).format('DD') || '';
                endFormattedDate = dayjs(endDate).format('DD MMMM');
            } else {
                startFormattedDate = dayjs(startDate).format('DD MMMM');
                endFormattedDate = dayjs(endDate).format('DD MMMM');
            }
        } else {
            startFormattedDate = dayjs(startDate).format('DD MMMM YYYY');
            endFormattedDate = dayjs(endDate).format('DD MMMM YYYY');
            if (startMonth === endMonth) {
                if (this.isFullMonthSelected(startDate, endDate)) {
                    const month = dayjs(endDate).format('MMMM YYYY');
                    return month.charAt(0).toUpperCase() + month.slice(1);
                }
                startFormattedDate = dayjs(startDate).format('DD') || '';
                endFormattedDate = dayjs(endDate).format('DD MMMM YYYY');
            } else {
                startFormattedDate = dayjs(startDate).format('DD MMMM YYYY');
                endFormattedDate = dayjs(endDate).format('DD MMMM YYYY');
            }
        }
        return this.localizationService
            .getLocalizedKey(
                'i18n_lib_commonBillingDateRange',
                {from: startFormattedDate, to: endFormattedDate}
            );
    }

    private setRange(from: Date, to: Date, anchor: Date = from): void {
        const [f, t] = this.singleMonth ? this.clampToMonth(from, to, anchor) : [from, to];
        this.form.setValue(this.applySelectedTime(f, t));
    }

    private applySelectedTime(from: Date, to: Date): {dateFrom: Date; dateTo: Date} {
        if (!this.withTime || !this.startTimeControl?.valid || !this.endTimeControl?.valid) {
            return {dateFrom: from, dateTo: to};
        }
        return {
            dateFrom: applyTime(startOfDay(from), this.startTimeControl.value),
            dateTo: applyTime(endOfDay(to), this.endTimeControl.value),
        };
    }

    private clampToMonth(from: Date, to: Date, anchor: Date): [Date, Date] {
        const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1, 0, 0, 0, 0);
        const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 23, 59, 59, 999);
        return [from < monthStart ? monthStart : from, to > monthEnd ? monthEnd : to];
    }

    private isFullMonthSelected(startDate: Date, endDate: Date): boolean {
        const lastDayOfMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
        return startDate.getFullYear() === endDate.getFullYear() &&
            startDate.getMonth() === endDate.getMonth() && startDate.getDate() === 1 &&
            endDate.getDate() === lastDayOfMonth;
    }


    private shiftMonth(multiplier: number): void {
        const currentMonth = this.getMonthRange();
        const previousMonth = this.getMonthRange(-1);
        const monthRange = this.getMonthRange(multiplier, this.form.value.dateFrom);
        if (monthRange[0].getTime() === previousMonth[0].getTime() && monthRange[1].getTime() === previousMonth[1].getTime()) {
            this.dateRange = PickerRange.PREVIOUS_MONTH;
        } else if (monthRange[0].getTime() === currentMonth[0].getTime() && monthRange[1].getTime() === currentMonth[1].getTime()) {
            this.dateRange = PickerRange.CURRENT_MONTH;
        }
        this.setRange(monthRange[0], monthRange[1]);
    }

    private getWeekRange(multiplier = 0, initialDate: Date = new Date()): [Date, Date] {
        const day = initialDate.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;

        const startOfWeek = new Date(initialDate);
        startOfWeek.setDate(initialDate.getDate() + (diffToMonday + multiplier));
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        return [startOfWeek, endOfWeek];
    }

    private getTodayRange(initialDate: Date = new Date()): [Date, Date] {
        const start = new Date();
        start.setDate(initialDate.getDate());
        start.setHours(0, 0, 0, 0);

        const end = new Date();
        end.setDate(start.getDate());
        end.setHours(23, 59, 59, 999);

        return [start, end];
    }

    private getMonthRange(multiplier = 0, initialDate: Date = new Date()): [Date, Date] {
        const month = initialDate.getMonth() + multiplier;
        const year = initialDate.getFullYear();
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);

        firstDayOfMonth.setHours(0, 0, 0, 0);
        lastDayOfMonth.setHours(23, 59, 59, 999);

        return [firstDayOfMonth, lastDayOfMonth];
    }
}

export interface DateRangeForm {
    dateFrom: FormControl<Date>;
    dateTo: FormControl<Date>;
}

export enum PickerRange {
    TODAY = 'TODAY',
    CURRENT_WEEK = 'CURRENT_WEEK',
    CURRENT_MONTH = 'CURRENT_MONTH',
    PREVIOUS_MONTH = 'PREVIOUS_MONTH',
    SELECT_RANGE = 'SELECT_RANGE'
}
